import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";

/* Desktop NavLink */
const DesktopNavLink = ({ link, isActive }) => (
    <Link
        to={link.path}
        aria-current={isActive ? "page" : undefined}
        className="relative group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
    >
        <span
            className={`absolute inset-0 rounded-lg transition-all duration-200 ${isActive
                ? "bg-amber-500/12 border border-amber-500/20"
                : "bg-transparent group-hover:bg-slate-800/60"
                }`}
        />
        <i
            className={`${link.icon} text-sm relative z-10 transition-colors duration-200 ${isActive ? "text-amber-400" : "text-slate-500 group-hover:text-slate-300"
                }`}
            aria-hidden="true"
        />
        <span
            className={`relative z-10 transition-colors duration-200 ${isActive ? "text-amber-400" : "text-slate-300 group-hover:text-white"
                }`}
        >
            {link.name}
        </span>
        {isActive && (
            <span className="relative z-10 w-1 h-1 rounded-full bg-amber-400 shadow-[0_0_5px_rgba(245,158,11,0.7)]" />
        )}
        <span
            className={`absolute bottom-0 left-2 right-2 h-px rounded-full bg-amber-500 transition-all duration-300 ${isActive ? "opacity-50 scale-x-100" : "opacity-0 scale-x-0 group-hover:scale-x-100 group-hover:opacity-30"
                }`}
        />
    </Link>
);

/* Mobile Drawer NavLink */
const MobileNavLink = ({ link, isActive, onClick }) => (
    <Link
        to={link.path}
        onClick={onClick}
        aria-current={isActive ? "page" : undefined}
        className={`group flex items-center gap-4 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${isActive
            ? "bg-gradient-to-r from-amber-500/15 to-amber-500/5 border border-amber-500/20 text-amber-400"
            : "text-slate-300 hover:bg-slate-800/50 hover:text-white border border-transparent"
            }`}
    >
        <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 ${isActive ? "bg-amber-500/20" : "bg-slate-800/60 group-hover:bg-slate-700/60"
                }`}
        >
            <i
                className={`${link.icon} text-base transition-colors ${isActive ? "text-amber-400" : "text-slate-400 group-hover:text-slate-200"
                    }`}
                aria-hidden="true"
            />
        </div>
        <span className="flex-1">{link.name}</span>
        {isActive ? (
            <span className="w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.6)]" />
        ) : (
            <i className="bi bi-chevron-right text-xs text-slate-600 group-hover:text-slate-400 transition-colors" aria-hidden="true" />
        )}
    </Link>
);

/* Hamburger icon */
const HamburgerIcon = ({ isOpen }) => (
    <div className="relative w-5 h-4 flex flex-col justify-between" aria-hidden="true">
        <span
            className={`block h-0.5 rounded-full bg-current transition-all duration-300 ease-in-out ${isOpen ? "rotate-45 translate-y-[7px] w-5" : "w-5"
                }`}
        />
        <span
            className={`block h-0.5 rounded-full bg-current transition-all duration-300 ease-in-out ${isOpen ? "opacity-0 scale-x-0" : "w-4"
                }`}
        />
        <span
            className={`block h-0.5 rounded-full bg-current transition-all duration-300 ease-in-out ${isOpen ? "-rotate-45 -translate-y-[7px] w-5" : "w-5"
                }`}
        />
    </div>
);

/* Main Navigation */
export default function Navigation() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [visible, setVisible] = useState(true);
    const lastScrollY = useRef(0);

    const location = useLocation();
    const currentPath = location.pathname;

    const mobileMenuRef = useRef(null);
    const menuButtonRef = useRef(null);

    useEffect(() => {
        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const y = window.scrollY;
                    setScrolled(y > 40);
                    setVisible(!(y > lastScrollY.current && y > 120));
                    lastScrollY.current = y;
                    const docH = document.documentElement.scrollHeight - window.innerHeight;
                    setScrollProgress(docH > 0 ? (y / docH) * 100 : 0);
                    ticking = false;
                });
                ticking = true;
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setIsOpen(false); }, [location.pathname]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && isOpen) setIsOpen(false); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen]);

    useEffect(() => {
        const onOutside = (e) => {
            if (
                isOpen &&
                mobileMenuRef.current &&
                !mobileMenuRef.current.contains(e.target) &&
                menuButtonRef.current &&
                !menuButtonRef.current.contains(e.target)
            ) setIsOpen(false);
        };
        document.addEventListener("mousedown", onOutside);
        return () => document.removeEventListener("mousedown", onOutside);
    }, [isOpen]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [isOpen]);

    const navLinks = [
        { name: "Home", path: "/", icon: "bi bi-house-door" },
        { name: "Services", path: "/services", icon: "bi bi-grid-3x3-gap" },
        { name: "Solutions", path: "/industries", icon: "bi bi-boxes" },
        { name: "About", path: "/about", icon: "bi bi-building" },
        { name: "Contact", path: "/contact", icon: "bi bi-envelope" },
    ];

    const isActiveLink = useCallback((path) => {
        if (path === "/") return currentPath === "/";
        return currentPath === path || currentPath.startsWith(path);
    }, [currentPath]);

    return (
        <>
            {/* Scroll Progress Bar */}
            <div className="fixed top-0 left-0 w-full h-[2px] z-[70] pointer-events-none">
                <div
                    className="h-full bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 transition-all duration-100 ease-linear shadow-[0_0_10px_rgba(245,158,11,0.5)]"
                    style={{ width: `${scrollProgress}%` }}
                />
            </div>

            {/* Header */}
            <header
                className={`fixed top-0 left-0 w-full z-50 transition-transform duration-500 ease-in-out ${visible ? "translate-y-0" : "-translate-y-full"
                    }`}
            >
                <div
                    className={`absolute inset-0 transition-all duration-500 ${scrolled
                        ? "bg-slate-950/80 backdrop-blur-2xl border-b border-white/[0.06] shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
                        : "bg-transparent"
                        }`}
                />
                <div
                    className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent transition-opacity duration-500 ${scrolled ? "opacity-100" : "opacity-0"
                        }`}
                />

                <nav className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
                    <div className="flex items-center justify-between h-16 md:h-[70px]">

                        {/* Logo */}
                        <Link
                            to="/"
                            className="flex items-center gap-2.5 group flex-shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded-lg p-1"
                            aria-label="Delight Galaxy Home"
                        >
                            <img
                                src={logo}
                                alt="Delight Galaxy"
                                className="h-24 w-auto object-contain transition-all duration-300 group-hover:scale-105 group-hover:drop-shadow-[0_0_8px_rgba(245,158,11,0.4)]"
                                onError={(e) => {
                                    e.target.style.display = "none";
                                    if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                                }}
                            />
                            <div className="hidden w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 items-center justify-center text-slate-950 font-black text-lg shadow-lg">
                                D
                            </div>
                        </Link>

                        {/* Desktop Nav pill group */}
                        <div
                            className="hidden md:flex items-center gap-1 p-1 rounded-xl bg-slate-900/40 border border-slate-800/40 backdrop-blur-sm"
                            role="menubar"
                        >
                            {navLinks.map((link) => (
                                <DesktopNavLink
                                    key={link.name}
                                    link={link}
                                    isActive={isActiveLink(link.path)}
                                />
                            ))}
                        </div>

                        {/* Desktop CTA */}
                        <div className="hidden md:flex items-center">
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 text-slate-950 text-sm font-bold shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:from-amber-400 hover:to-amber-300 active:scale-95 transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                            >
                                Get Started
                                <i className="bi bi-arrow-right text-xs" aria-hidden="true" />
                            </Link>
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            ref={menuButtonRef}
                            onClick={() => setIsOpen((o) => !o)}
                            className="md:hidden relative w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all duration-200 border border-slate-700/50 bg-slate-800/60 hover:bg-slate-700/80 active:scale-95 outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                            aria-expanded={isOpen}
                            aria-controls="mobile-menu"
                            aria-label={isOpen ? "Close menu" : "Open menu"}
                        >
                            <HamburgerIcon isOpen={isOpen} />
                        </button>
                    </div>
                </nav>
            </header>

            {/* Mobile Backdrop */}
            <div
                className={`md:hidden fixed inset-0 z-40 transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                    }`}
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
            >
                <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" />
            </div>

            {/* Mobile Drawer (right slide-in) */}
            <div
                id="mobile-menu"
                ref={mobileMenuRef}
                role="dialog"
                aria-modal="true"
                aria-label="Mobile navigation"
                className={`md:hidden fixed top-0 right-0 bottom-0 z-50 w-[min(320px,85vw)] transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                <div className="flex flex-col h-full bg-slate-950 border-l border-slate-800/60 shadow-[-20px_0_60px_rgba(0,0,0,0.6)]">

                    {/* Drawer Header */}
                    <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-800/60">
                        <img
                            src={logo}
                            alt="Delight Galaxy"
                            className="h-7 w-auto object-contain"
                            onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <button
                            onClick={() => setIsOpen(false)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                            aria-label="Close navigation"
                        >
                            <i className="bi bi-x-lg text-sm" aria-hidden="true" />
                        </button>
                    </div>

                    {/* Amber accent rule */}
                    <div className="mx-5 mt-1 h-px bg-gradient-to-r from-amber-500/40 via-amber-500/10 to-transparent rounded-full" />

                    {/* Nav Links */}
                    <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1" aria-label="Mobile page links">
                        {navLinks.map((link) => (
                            <MobileNavLink
                                key={link.name}
                                link={link}
                                isActive={isActiveLink(link.path)}
                                onClick={() => setIsOpen(false)}
                            />
                        ))}
                    </nav>

                    {/* Drawer Footer CTA */}
                    <div className="px-4 py-5 border-t border-slate-800/60 space-y-3">
                        <Link
                            to="/contact"
                            onClick={() => setIsOpen(false)}
                            className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-amber-500 to-amber-400 text-slate-950 py-3.5 rounded-xl font-bold text-sm hover:from-amber-400 hover:to-amber-300 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-amber-500/20 outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                        >
                            <i className="bi bi-lightning-charge-fill text-sm" aria-hidden="true" />
                            Request Consultation
                            <i className="bi bi-arrow-right text-xs" aria-hidden="true" />
                        </Link>
                        <p className="text-center text-[10px] text-slate-600 tracking-wide uppercase">
                            Enterprise Solutions · Kampala, Uganda
                        </p>
                    </div>
                </div>
            </div>

            {/* WhatsApp FAB */}
            <a
                href="https://wa.me/256704698298"
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-6 right-6 z-50 group"
                aria-label="Chat with us on WhatsApp"
            >
                <span className="absolute inset-0 rounded-full bg-green-500/25 animate-ping" aria-hidden="true" />
                <div className="relative flex items-center overflow-hidden rounded-full bg-green-500 text-white shadow-xl shadow-green-500/30 hover:shadow-green-400/50 transition-all duration-300 group-hover:scale-105 active:scale-95 pr-[2px]">
                    <span className="max-w-0 group-hover:max-w-[110px] overflow-hidden whitespace-nowrap text-sm font-semibold transition-all duration-300 ease-in-out opacity-0 group-hover:opacity-100 group-hover:pl-4">
                        Chat with us
                    </span>
                    <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
                        <i className="bi bi-whatsapp text-2xl" aria-hidden="true" />
                    </div>
                </div>
            </a>
        </>
    );
}
